import {
  type EncounterPausedReasonCode,
  GroupSecurity,
} from '@quromedical/fhir-common';
import type {
  AdmissionGetResponseWithVitals,
  AdmissionUpdateRequest,
  CreateUploadUrlRequest,
  CreateUploadUrlResponse,
} from '@quromedical/models';
import { ConfirmationDialog } from 'components/input';
import { WithGroup } from 'core/auth';
import { handleDownload } from 'core/media';
import {
  Alert,
  Skeleton,
  SkeletonProvider,
  Snackbar,
  Spacer,
  Visible,
} from 'design-system';
import { BASE_SIZE } from 'design-system/theme';
import { logger } from 'helpers';
import { useFiniteState } from 'hooks/useFiniteState';
import { useToggle } from 'hooks/useToggle';
import { icd10CodeFetcher } from 'integration';
import { AdmissionApi, MediaApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback, useState } from 'react';
import { strings } from 'strings';
import { trpcClient } from 'trpc';
import { AdmissionCard } from './AdmissionCard';
import { AdmissionPausedReason } from './AdmissionPauseReasons';
import { AdmissionStart, type AdmissionStartData } from './AdmissionStart';
import { AdmissionUpdateForm } from './AdmissionUpdateForm';

interface AdmissionInlineUpdateCardProps {
  patientId: string;
  canEdit: boolean;
  canViewMedia: boolean;
  data: AdmissionGetResponseWithVitals;
  revalidate: () => void;
  onSecondarySubmit?: () => void;
  onTertiarySubmit?: (admissionId: string) => void;
  secondaryButtonText?: string;
  tertiaryButtonText?: string;
  showSecondarySubmitButton?: boolean;
}

const admissionApi = new AdmissionApi();
const mediaApi = new MediaApi();

type FormState = 'initial' | 'editing' | 'submitting' | 'error';
type AdmissionPausedReasonData = { statusReason: EncounterPausedReasonCode };

export const AdmissionInlineUpdateCard: React.FC<
  AdmissionInlineUpdateCardProps
> = ({
  patientId,
  data,
  revalidate,
  canEdit,
  canViewMedia,
  onTertiarySubmit,
  tertiaryButtonText,
  showSecondarySubmitButton,
}) => {
  const state = useFiniteState<FormState>('initial');
  const [isPauseDialog, setPauseDialog] = useToggle(false);
  const [isResumeDialog, setResumeDialog] = useToggle(false);
  const [isStartDialog, setStartDialog] = useToggle(false);

  const [pausedReasonState, setPausedReasonState] = useState<
    AdmissionPausedReasonData | undefined
  >(undefined);
  const [admissionStartState, setAdmissionStartState] = useState<
    AdmissionStartData | undefined
  >(undefined);

  const handleSubmit = useCallback(
    async (result: AdmissionUpdateRequest) => {
      state.set('submitting');
      try {
        await admissionApi.updateAdmission(result);

        state.set('initial');
        revalidate();
      } catch (err) {
        state.set('error');
      }
    },
    [revalidate, state],
  );

  const downloadFile = useCallback(
    async (id: string) => {
      try {
        const { url } = await mediaApi.createDownloadUrl(patientId, id);
        await handleDownload(url);
      } catch (err) {
        logger.error(err);
      }
    },
    [patientId],
  );

  const onPaused = useCallback(async () => {
    if (!pausedReasonState?.statusReason) {
      logger.error('No reason provided for pausing admission');
      return;
    }

    await trpcClient.admission.pause.mutate({
      id: data.id,
      statusReason: pausedReasonState?.statusReason,
    });
    revalidate();
  }, [data.id, revalidate, pausedReasonState?.statusReason]);

  const onResumed = useCallback(async () => {
    await trpcClient.admission.resume.mutate({ id: data.id });
    revalidate();
  }, [data.id, revalidate]);

  const onStarted = useCallback(async () => {
    if (!admissionStartState?.periodStart) {
      logger.error('No start date provided for starting admission');
      return;
    }
    await trpcClient.admission.start.mutate({
      id: data.id,
      periodStart: admissionStartState.periodStart,
    });
    revalidate();
  }, [data.id, admissionStartState, revalidate]);

  const createUploadUrl = useCallback(
    (body: CreateUploadUrlRequest): Promise<CreateUploadUrlResponse> =>
      mediaApi.createUploadUrl(patientId, body),
    [patientId],
  );

  const onSecondarySubmit =
    data.status === 'onleave'
      ? setResumeDialog
      : data.status === 'planned'
        ? setStartDialog
        : setPauseDialog;

  const buttonText =
    data.status === 'onleave'
      ? strings.AdmissionResume
      : data.status === 'planned'
        ? strings.AdmissionStart
        : strings.AdmissionPause;

  return (
    <>
      <SkeletonProvider loading={state.is('submitting')}>
        <Skeleton>
          <Visible if={state.isNot('editing')}>
            <AdmissionCard
              patientId={patientId}
              downloadFile={downloadFile}
              data={data}
              canEdit={canEdit}
              canViewMedia={canViewMedia}
              onEditPress={state.next('editing')}
              onSecondarySubmit={onSecondarySubmit}
              onTertiarySubmit={() => onTertiarySubmit?.(data.id)}
              secondaryButtonText={buttonText}
              tertiaryButtonText={tertiaryButtonText}
              showSecondarySubmitButton={showSecondarySubmitButton}
            />
          </Visible>

          <Visible if={state.is('editing')}>
            <AdmissionUpdateForm
              icd10CodeFetcher={icd10CodeFetcher}
              data={data}
              handleCancel={state.next('initial')}
              handleSubmit={handleSubmit}
              createUploadUrl={createUploadUrl}
            />
          </Visible>
        </Skeleton>
      </SkeletonProvider>

      <Visible if={isPauseDialog}>
        <WithGroup groups={[GroupSecurity.EncounterUpdate]}>
          <ConfirmationDialog
            onSubmit={onPaused}
            buttonText={strings.AdmissionPause}
            title={strings.AdmissionPausedDialogTitle}
            message={strings.AdmissionPausedDialogMessage}
            status="status-critical"
            setDialogState={setPauseDialog}
          >
            <AdmissionPausedReason onChange={setPausedReasonState} />
            <Spacer height={BASE_SIZE[148]} />
          </ConfirmationDialog>
        </WithGroup>
      </Visible>

      <Visible if={isResumeDialog}>
        <WithGroup groups={[GroupSecurity.EncounterUpdate]}>
          <ConfirmationDialog
            onSubmit={onResumed}
            buttonText={strings.AdmissionResume}
            title={strings.AdmissionResumeDialogTitle}
            message={strings.AdmissionResumeDialogMessage}
            status="status-critical"
            setDialogState={setResumeDialog}
          />
        </WithGroup>
      </Visible>

      <Visible if={isStartDialog}>
        <WithGroup groups={[GroupSecurity.EncounterUpdate]}>
          <ConfirmationDialog
            onSubmit={onStarted}
            buttonText={strings.AdmissionStart}
            title={strings.AdmissionStartPlannedTitle}
            message={strings.AdmissionStartPlannedMessage}
            status="status-critical"
            setDialogState={setStartDialog}
          >
            <AdmissionStart onChange={setAdmissionStartState} />
          </ConfirmationDialog>
        </WithGroup>
      </Visible>

      <Snackbar onClose={state.next('editing')} isOpen={state.is('error')}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={state.next('editing')}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    </>
  );
};
